import { Theme, useMediaQuery } from '@mui/material';
import { AppliedGiftCard, CompletedPaymentInfo, OctoOrder, PaymentInfo } from '@repo/types';
import { createContext, PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react';
import { useAtom } from 'ximple';
import { loadPurchase } from 'src/state/payment/purchase.localstorage';
import {
    createInitialCompletedPaymentInfo,
    validateCheckoutInfoData,
} from '@repo/widget-utils/checkout-info-helper';
import { useConfigurations, useMembershipMode } from '@repo/widget-utils/widgetsConfiguration';
import { checkoutInfoAtom } from 'src/state/checkout-info/checkoutInfoAtom';
import { cartAtom } from 'src/state/cart/cartAtom';
import { nativeMemberCoverageAtom } from 'src/state/native-membership/nativeMemberCoverageAtom';
import { voucherAtom } from 'src/state/voucher/voucherAtom';
import { paymentPlanAtom } from 'src/state/payment-plan/paymentPlanAtom';
import { CheckoutTabType, IdxOpts } from './views/getTabIndex';
import {
    PaymentRedirectContext,
    tryFindPaymentRedirectContext,
} from '@repo/widget-utils/payment-redirect-helper';
import { calculateAppliedGiftcards, PriceSummary } from '@repo/widget-utils/price-helper';
import { usePriceSummary } from 'src/hooks/domain/usePriceSummary';
import useOctoOrderInCart from 'src/hooks/domain/cart/useOctoOrderInCart';
import useHasOctoExtras from 'src/hooks/domain/useHasOctoExtras';

type CartCheckoutContextState = {
    activeTab: CheckoutTabType;
    setActiveTab: (tab: CheckoutTabType) => void;
    paymentInfo: PaymentInfo | null;
    setPaymentInfo: (paymentInfo: PaymentInfo | null) => void;
    completedPaymentInfo: CompletedPaymentInfo | null;
    setCompletedPaymentInfo: (completedPaymentInfo: CompletedPaymentInfo | null) => void;
    isMakingReservation: boolean;
    setIsMakingReservation: (isMakingReservation: boolean) => void;
    paymentRedirectContext: PaymentRedirectContext | null;
    priceSummary: PriceSummary;
    appliedGiftcards: AppliedGiftCard[];
    relatedProductIds: string[];
    idxOpts: IdxOpts;
    showAddressFields: boolean;
    largeScreen: boolean;
    onConfirmationPage: boolean;
    isCheckoutInfoDataValid: boolean;
    octoOrderInCart?: OctoOrder;
    isLoadingOctoOrderInCart?: boolean;
};

const CartCheckoutContext = createContext<CartCheckoutContextState | undefined>(undefined);

export type CartCheckoutContextProps = PropsWithChildren;
export function CartCheckoutContextProvider(props: CartCheckoutContextProps) {
    const config = useConfigurations();

    const [cartItems] = useAtom(cartAtom);
    const [{ giftcards, appliedPromoCode }] = useAtom(voucherAtom);
    const [{ isUsingPaymentPlan }] = useAtom(paymentPlanAtom);
    const [checkoutInfo] = useAtom(checkoutInfoAtom);
    const [{ result: nativeCoverage, committedResult: nativeCommittedCoverage }] =
        useAtom(nativeMemberCoverageAtom);
    const membershipMode = useMembershipMode();

    const { octoOrder: octoOrderInCart, isLoading: isLoadingOctoOrderInCart } =
        useOctoOrderInCart();

    const paymentRedirectContext = tryFindPaymentRedirectContext();
    const storedPurchase = loadPurchase();
    const initialCompletedPaymentInfo = createInitialCompletedPaymentInfo(
        storedPurchase,
        paymentRedirectContext,
    );

    const isPackageInCart = Object.values(cartItems).some((item) => item.pkg);

    const largeScreen = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
    const [isMakingReservation, setIsMakingReservation] = useState<boolean>(false);
    const [paymentInfo, setPaymentInfo] = useState<PaymentInfo | null>(null);
    const [completedPaymentInfo, setCompletedPaymentInfo] = useState<CompletedPaymentInfo | null>(
        initialCompletedPaymentInfo,
    );

    const itemsWithExtras = cartItems.filter((item) =>
        item.products.some(
            (p) =>
                p.extraProducts.length > 0 || p.timeslots.some((t) => t.extraProducts.length > 0),
        ),
    );
    const hasExtras = (config.enableExtras && itemsWithExtras.length > 0) || false;
    const hasOctoExtras = useHasOctoExtras(octoOrderInCart);

    // Collection grouping merges related products from all products, but we only
    // want related products for the specific products the user booked. Each ticket
    // option references its original (pre-grouping) product instance.
    const [relatedProductIds] = useState(
        Array.from(
            new Set(
                cartItems.flatMap((item) =>
                    item.ticketOptions
                        .filter((t) => t.quantity > 0)
                        .flatMap((t) =>
                            t.productInstances.flatMap((pi) =>
                                pi.relatedProducts.map((r) => r.toString()),
                            ),
                        ),
                ),
            ),
        ).filter((id) => !cartItems.some((item) => item.products[0]?.product?.id === id)),
    );

    // Native membership: a dedicated "Velg betalingsmetode" step appears when the member has
    // eligible member-card coverage for this cart (inert for guest/legacy/none).
    //
    // From reserve onwards the step is derived from the snapshot committed at reserve, not the live
    // /calculate result: a live update landing mid-payment (the reservation now holds the punches,
    // so eligibility comes back empty) would renumber the tabs and remount the active payment panel,
    // destroying the payment gateway iframe. isMakingReservation covers the invoice/skip-payment
    // window where the cart clears (nulling the live result) before the confirmation state is set.
    const freezeMemberCardStep =
        isMakingReservation || paymentInfo !== null || completedPaymentInfo !== null;
    const coverageForStep = freezeMemberCardStep
        ? (nativeCommittedCoverage ?? nativeCoverage)
        : nativeCoverage;
    const hasMemberCardStep = useMemo(() => {
        const memberCard = coverageForStep?.member_card;
        return (
            membershipMode === 'native' &&
            !!memberCard &&
            memberCard.per_line.some((line) => line.eligible_card_ids.length > 0)
        );
    }, [membershipMode, coverageForStep]);

    const idxOpts: IdxOpts = useMemo(
        () => ({
            hasExtras: hasExtras,
            hasOctoExtras: hasOctoExtras,
            hasPackage: isPackageInCart,
            display: true,
            isUsingPaymentPlan,
            hasRelatedProducts: !!config.enableRelatedProducts && relatedProductIds.length > 0,
            hasMemberCardStep,
        }),
        [
            hasExtras,
            hasOctoExtras,
            isPackageInCart,
            isUsingPaymentPlan,
            config.enableRelatedProducts,
            relatedProductIds,
            hasMemberCardStep,
        ],
    );

    const initialActiveTab =
        paymentRedirectContext !== null
            ? 'payment'
            : idxOpts.hasPackage
              ? 'package'
              : idxOpts.hasRelatedProducts
                ? 'relatedproducts'
                : idxOpts.hasExtras
                  ? 'extras'
                  : idxOpts.hasOctoExtras
                    ? 'octoextras'
                    : 'contactinfo';
    const [activeTab, setActiveTab] = useState<CheckoutTabType>(initialActiveTab);

    // If the member-card step disappears (cart edited away its eligible coverage) while it's the
    // active tab, fall back to contact info so the tab index never resolves to -1.
    useEffect(() => {
        if (activeTab === 'membercard' && !hasMemberCardStep) setActiveTab('contactinfo');
    }, [activeTab, hasMemberCardStep]);

    const showAddressFields = cartItems.some((item) =>
        item.products.some(
            (p) => p.product?.type === 'accommodation' || p.product?.type === 'timeslot',
        ),
    );

    const priceSummary = usePriceSummary(
        cartItems,
        isLoadingOctoOrderInCart,
        octoOrderInCart,
        appliedPromoCode,
    );

    const appliedGiftcards = useMemo(
        () => calculateAppliedGiftcards(giftcards, priceSummary.totalPrice),
        [giftcards, priceSummary.totalPrice],
    );

    const onConfirmationPage = completedPaymentInfo !== null;

    const isCheckoutInfoDataValid = useMemo(() => {
        if (checkoutInfo === null) return false;
        return validateCheckoutInfoData(checkoutInfo, false, cartItems);
    }, [checkoutInfo, cartItems]);

    return (
        <>
            <CartCheckoutContext.Provider
                value={{
                    activeTab,
                    setActiveTab,
                    paymentRedirectContext,
                    priceSummary,
                    appliedGiftcards,
                    relatedProductIds,
                    paymentInfo,
                    setPaymentInfo,
                    completedPaymentInfo,
                    setCompletedPaymentInfo,
                    isMakingReservation,
                    setIsMakingReservation,
                    idxOpts,
                    showAddressFields,
                    largeScreen,
                    onConfirmationPage,
                    isCheckoutInfoDataValid,
                    octoOrderInCart: octoOrderInCart,
                    isLoadingOctoOrderInCart: isLoadingOctoOrderInCart,
                }}
            >
                {props.children}
            </CartCheckoutContext.Provider>
        </>
    );
}

export function useCartCheckoutContext(): CartCheckoutContextState {
    const context = useContext(CartCheckoutContext);
    if (context === undefined) {
        throw new Error('useCartCheckoutContext must be used within a CartCheckoutContextProvider');
    }
    return context;
}
